import { preferNonEmpty } from '../utils/prefer_non_empty';
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const getPathValue = (source, path) => {
    if (!source) {
        return undefined;
    }
    if (path in source) {
        return source[path];
    }
    return path.split('.').reduce((value, key) => {
        if (!isRecord(value)) {
            return undefined;
        }
        return value[key];
    }, source);
};
export const getDocumentValue = (document, path) => {
    if (!isRecord(document)) {
        return undefined;
    }
    const data = isRecord(document.data) ? document.data : undefined;
    return getPathValue(document, path) ?? getPathValue(data, path);
};
const getCollectionLength = (value) => {
    if (Array.isArray(value)) {
        return value.length;
    }
    if (isRecord(value) && Array.isArray(value.data)) {
        return value.data.length;
    }
    return undefined;
};
/**
 * Classic site UI showed Applied Signers as a number. Prefer the Number property when
 * Cortex returns it; otherwise fall back to signature / accepted-signer collection size.
 */
export const getAppliedSignersCount = (document) => {
    const appliedSigners = getDocumentValue(document, 'ec__applied_signers');
    if (typeof appliedSigners === 'number') {
        return appliedSigners;
    }
    const signaturesLength = getCollectionLength(getDocumentValue(document, 'ec__signatures'));
    if (typeof signaturesLength === 'number') {
        return signaturesLength;
    }
    const acceptedSignersLength = getCollectionLength(getDocumentValue(document, 'ec__accepted_signers'));
    if (typeof acceptedSignersLength === 'number') {
        return acceptedSignersLength;
    }
    return 0;
};
export const normalizeSiteDocument = (rawDocument) => {
    const document = isRecord(rawDocument) ? rawDocument : {};
    const data = isRecord(document.data) ? document.data : {};
    const primaryParticipant = getDocumentValue(document, 'ec__primary_participant') ?? {};
    const documentTemplate = getDocumentValue(document, 'ec__document_template') ?? {};
    const identifier = getDocumentValue(document, 'ec__identifier') ?? '';
    const participantNameOrEmail = getDocumentValue(document, 'ec__primary_participant.c_participant_name_or_email') ?? '';
    const participantId = getDocumentValue(document, 'ec__primary_participant.c_number') ?? '';
    const title = getDocumentValue(document, 'ec__document_template.ec__title') ?? '';
    const status = getDocumentValue(document, 'ec__status') ?? '';
    const appliedSigners = getAppliedSignersCount(document);
    // Studio pins these per version on the runtime; its document template is a shell shared by every version.
    const sourcePath = isRecord(getDocumentValue(document, 'ec__published_studio_template'))
        ? 'ec__published_studio_template'
        : 'ec__document_template';
    const sourceVersion = getDocumentValue(document, `${sourcePath}.ec__committee_approved_version`) ?? '';
    const sourceLabel = getDocumentValue(document, `${sourcePath}.ec__committee_approved_version_label`) ?? '';
    return {
        ...document,
        ...data,
        _id: getDocumentValue(document, '_id') ?? '',
        ec__identifier: identifier,
        identifier,
        participantNameOrEmail,
        participantId,
        title,
        status,
        appliedSigners,
        sourceVersion,
        sourceLabel,
        ec__primary_participant: {
            ...primaryParticipant,
            c_participant_name_or_email: participantNameOrEmail,
            c_number: participantId,
        },
        ec__document_template: {
            ...documentTemplate,
            ec__title: title,
        },
        ec__required_signers: getDocumentValue(document, 'ec__required_signers') ?? 0,
        ec__applied_signers: getDocumentValue(document, 'ec__applied_signers'),
        ec__signatures: getDocumentValue(document, 'ec__signatures'),
        ec__accepted_signers: getDocumentValue(document, 'ec__accepted_signers'),
        ec__status: status,
        created: getDocumentValue(document, 'created') ?? '',
        ec__pts: getDocumentValue(document, 'ec__pts'),
        ec__pts_signature_date_wallclock: getDocumentValue(document, 'ec__pts_signature_date_wallclock'),
        ec__final_document: getDocumentValue(document, 'ec__final_document'),
    };
};
const OPEN_DOCUMENT_STATUSES = ['sent', 'partial'];
const AWAITING_SIGNER_STATUSES = ['connected', 'partial'];
/**
 * The indexed Cortex paths behind each sortable column, in sort priority, so the
 * server sorts the whole result set. Every path must be a property of the signed
 * document itself: Cortex rejects a sort through a referenced record.
 */
export const SITE_DOCUMENT_SORT_PATHS = {
    identifier: ['ec__identifier'],
    // Indexed copies of the live participant and title values. Source version and source label
    // have no copy: the signed document is already at the index limit.
    participantNameOrEmail: ['ec__sort_participant_name'],
    participantId: ['ec__sort_participant_number'],
    title: ['ec__sort_title'],
    status: ['ec__status'],
    ec__pts: ['ec__pts'],
    ec__required_signers: ['ec__required_signers'],
    appliedSigners: ['ec__applied_signers'],
    // Uploaded first, then printed, so digital, printed-awaiting-upload and uploaded documents stay in separate groups.
    ec__final_document: ['ec__pts_document_uploaded', 'ec__pts'],
    created: ['created'],
};
/**
 * Document properties the search box matches directly. Cortex can only match
 * properties stored on the signed document itself: a path through
 * `ec__primary_participant` or `ec__document_template` fails the whole list with
 * `kInvalidArgument`. Participant and title matches are resolved to record ids
 * first (see `matchSiteDocumentReferences`) and matched with `$in`.
 */
export const SITE_DOCUMENT_SEARCH_PATHS = ['ec__identifier'];
/** The linked-record columns the search also spans, read once per site to resolve ids. */
export const SITE_DOCUMENT_SEARCH_REFERENCE_PATHS = [
    'ec__primary_participant.c_participant_name_or_email',
    'ec__primary_participant.c_number',
    'ec__document_template.ec__title',
];
/** Cortex rejects `$regex` strings over 255 characters; keep a margin for escaping. */
export const SITE_DOCUMENTS_SEARCH_MAX_LENGTH = 100;
/**
 * Ids travel in the list GET's query string. 150 keeps the URL around 6 KB with
 * every table path, under the 8 KB request-line limit common to proxies.
 */
export const SITE_DOCUMENTS_SEARCH_MAX_MATCHED_IDS = 150;
const escapeRegExp = (value) => value.replace(/[/.*+?^${}()|[\]\\]/g, '\\$&');
const normalizeSearch = (searchValue) => searchValue?.trim().slice(0, SITE_DOCUMENTS_SEARCH_MAX_LENGTH) ?? '';
const startsWithIgnoringCase = (value, prefix) => typeof value === 'string' && value.toLowerCase().startsWith(prefix);
/**
 * Participant and template ids whose name, number or title starts with the
 * search, matching the identifier's case-insensitive prefix match. Templates go
 * first in the id budget: a site has few, and each one can cover many documents.
 */
export const matchSiteDocumentReferences = (rawDocuments, searchValue) => {
    const search = normalizeSearch(searchValue).toLowerCase();
    const participantIds = new Set();
    const templateIds = new Set();
    if (search) {
        for (const document of rawDocuments) {
            if (!isRecord(document)) {
                continue;
            }
            const participantId = getDocumentValue(document, 'ec__primary_participant._id');
            const templateId = getDocumentValue(document, 'ec__document_template._id');
            if (participantId &&
                (startsWithIgnoringCase(getDocumentValue(document, 'ec__primary_participant.c_participant_name_or_email'), search) ||
                    startsWithIgnoringCase(getDocumentValue(document, 'ec__primary_participant.c_number'), search))) {
                participantIds.add(participantId);
            }
            if (templateId && startsWithIgnoringCase(getDocumentValue(document, 'ec__document_template.ec__title'), search)) {
                templateIds.add(templateId);
            }
        }
    }
    const keptTemplateIds = [...templateIds].slice(0, SITE_DOCUMENTS_SEARCH_MAX_MATCHED_IDS);
    const keptParticipantIds = [...participantIds].slice(0, SITE_DOCUMENTS_SEARCH_MAX_MATCHED_IDS - keptTemplateIds.length);
    return {
        participantIds: keptParticipantIds,
        templateIds: keptTemplateIds,
        truncated: keptTemplateIds.length + keptParticipantIds.length < templateIds.size + participantIds.size,
    };
};
const toCollection = (value) => {
    if (Array.isArray(value)) {
        return value.filter(isRecord);
    }
    if (isRecord(value) && Array.isArray(value.data)) {
        return value.data.filter(isRecord);
    }
    return [];
};
/**
 * Translate the table's sorting state into a Cortex sort. Falls back to
 * newest-first, which is the list's resting order.
 */
export const buildSiteDocumentsSort = (sorting = []) => {
    const [primary] = sorting;
    const paths = primary && SITE_DOCUMENT_SORT_PATHS[primary.id];
    if (!paths) {
        return { created: -1, _id: -1 };
    }
    const direction = primary.desc ? -1 : 1;
    return { ...Object.fromEntries(paths.map((path) => [path, direction])), _id: -1 };
};
/**
 * Nucleus Table only draws the pager when `pageCount > 1`. The Cortex
 * site-documents list returns `hasMore` and never `total` (`?total=true` does
 * not change that), so `hasMore` can only reveal one following page at a time.
 */
export const siteDocumentsPageCount = ({ hasMore = false, pageIndex, rowCount, }) => {
    // An empty page reveals nothing about how many pages exist.
    if (rowCount === 0) {
        return 0;
    }
    return hasMore ? pageIndex + 2 : pageIndex + 1;
};
/**
 * The `ec__status` values the list may return, or undefined when nothing narrows them. The
 * awaiting filter and the Status filter combine as an intersection so both stay one `$in`.
 * An empty array means no status can match.
 */
export const resolveSiteDocumentsStatuses = (isAwaitingMySignature, statuses = []) => {
    if (!isAwaitingMySignature) {
        return statuses.length > 0 ? [...statuses] : undefined;
    }
    return statuses.length > 0
        ? OPEN_DOCUMENT_STATUSES.filter((status) => statuses.some((selected) => selected === status))
        : OPEN_DOCUMENT_STATUSES;
};
export const buildSiteDocumentsWhere = (siteId, awaitingAccountId, searchValue, references, statuses) => {
    const where = { ec__site: siteId };
    const statusIn = resolveSiteDocumentsStatuses(!!awaitingAccountId, statuses);
    // An empty intersection matches nothing. Omit it rather than sending `$in: []`, which Cortex rejects.
    if (statusIn && statusIn.length > 0) {
        where.ec__status = { $in: statusIn };
    }
    if (awaitingAccountId) {
        where.ec__accepted_signers = {
            $elemMatch: {
                ec__account: awaitingAccountId,
                // Cortex rejects restricted match operators ($ne, $nin) on client where
                // unless the org allowlists them. $in and $elemMatch are unrestricted.
                ec__status: { $in: AWAITING_SIGNER_STATUSES },
            },
        };
    }
    const search = normalizeSearch(searchValue);
    if (search) {
        // Prefix match, same as classic site-app filters, so Cortex can use an index.
        const pattern = `/^${escapeRegExp(search)}/i`;
        where.$or = [
            ...SITE_DOCUMENT_SEARCH_PATHS.map((path) => ({ [path]: { $regex: pattern } })),
            ...(references?.participantIds.length ? [{ ec__primary_participant: { $in: references.participantIds } }] : []),
            ...(references?.templateIds.length ? [{ ec__document_template: { $in: references.templateIds } }] : []),
        ];
    }
    return where;
};
const INTERNAL_SIGNER_TYPE = 'internal signer';
/**
 * Whether the document is still waiting on somebody at the site, as opposed to
 * the participant or a third party.
 *
 * Read off the template's signer roles rather than the applied/required counts:
 * those counts cannot say *who* is outstanding, so a document waiting only on
 * the participant looked identical to one waiting on the site. An internal role
 * with no accepted-signer row yet has not signed either, so absence counts as
 * outstanding.
 */
export const isAwaitingSiteUserSignature = (document) => {
    if (!isRecord(document)) {
        return false;
    }
    const status = getDocumentValue(document, 'ec__status');
    if (!status || !OPEN_DOCUMENT_STATUSES.includes(status)) {
        return false;
    }
    const signerRoles = preferNonEmpty(toCollection(getDocumentValue(document, 'ec__published_studio_template.ec__signer_roles')), toCollection(getDocumentValue(document, 'ec__document_template.ec__signer_roles')));
    const internalRoles = signerRoles
        .filter((role) => role.ec__signer_type === INTERNAL_SIGNER_TYPE && role.ec__required !== false)
        .map((role) => role.ec__role);
    if (internalRoles.length === 0) {
        return false;
    }
    const completedRoles = new Set(toCollection(getDocumentValue(document, 'ec__accepted_signers'))
        .filter((signer) => signer.ec__status === 'complete')
        .map((signer) => signer.ec__signer_role));
    return internalRoles.some((role) => !completedRoles.has(role));
};
